import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Lock, IndianRupee, Sparkles } from "lucide-react";
import { createPaymentOrder, verifyPayment, getPricingConfig } from "@/lib/api";
import CallbackDialog from "@/components/CallbackDialog";

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (typeof window.Razorpay !== "undefined") return resolve(true);
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const formatRupees = (paise) => {
  if (!paise && paise !== 0) return "—";
  return `₹${(paise / 100).toLocaleString("en-IN")}`;
};

/**
 * Locks downloads until the user completes a Razorpay payment.
 * Renders a polished checkout block. On success calls onPaid().
 */
export default function PaymentGate({
  willId,
  testator,
  userEmail = "",
  userPhone = "",
  onPaid,
}) {
  const [config, setConfig] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    getPricingConfig()
      .then((c) => mounted && setConfig(c))
      .catch(() => mounted && setConfig({ razorpay_configured: false }));
    return () => {
      mounted = false;
    };
  }, []);

  const startPayment = async () => {
    if (!agreed) {
      toast.error("Please confirm the Terms, Disclaimer and Refund Policy to proceed.");
      return;
    }
    if (config && !config.razorpay_configured) {
      toast.error(
        "Online payments are being set up. Please request a callback and our team will assist."
      );
      return;
    }
    setLoading(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load Razorpay. Check your connection.");
      const order = await createPaymentOrder({
        will_id: willId,
        plan: "essential",
        user_email: userEmail,
      });

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "WillSeva",
        description: "Essential Will",
        order_id: order.order_id,
        prefill: {
          name: testator || "",
          email: userEmail || "",
          contact: (userPhone || "").replace(/\D/g, "").slice(-10),
        },
        notes: { will_id: willId },
        theme: { color: "#0A1128" },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast.message("Payment cancelled. You can retry anytime.");
          },
        },
        handler: async (response) => {
          try {
            await verifyPayment({
              will_id: willId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Payment successful — your will is unlocked.");
            onPaid?.({
              payment_id: response.razorpay_payment_id,
              order_id: response.razorpay_order_id,
            });
          } catch (e) {
            toast.error(
              e?.response?.data?.detail || "Payment verification failed. Please contact support."
            );
          } finally {
            setLoading(false);
          }
        },
      });
      rzp.on("payment.failed", (resp) => {
        setLoading(false);
        toast.error(
          `Payment failed: ${resp?.error?.description || "please try another method."}`
        );
      });
      rzp.open();
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data?.detail || e?.message || "Could not start payment.");
    }
  };

  const price = config?.essential_will_paise;

  return (
    <Card
      className="relative overflow-hidden border-gold/50 shadow-[0_18px_44px_-22px_rgba(197,160,89,0.55)]"
      data-testid="payment-gate"
    >
      <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-gold/15 blur-3xl" />
      <CardContent className="relative p-6 sm:p-10">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-md bg-navy text-gold">
            <Lock size={18} />
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
              Final Step
            </div>
            <div className="mt-1 font-serif text-2xl text-navy">
              Complete Payment to Unlock Your Will
            </div>
          </div>
        </div>

        <p className="mt-4 text-sm text-[#4A5568]">
          Your will has been professionally drafted and is securely stored against your reference ID.
          Complete a secure payment to unlock the PDF &amp; DOCX downloads. Includes free edits for 30 days.
        </p>

        <div className="mt-6 rounded-md border border-[#0A1128]/10 bg-white p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-serif text-lg text-navy">Essential Will</div>
              <div className="text-xs text-[#4A5568]">No calls. No waiting. Done today.</div>
            </div>
            <div className="flex items-baseline gap-1 text-navy">
              <IndianRupee size={18} />
              <span className="font-serif text-3xl">
                {price ? (price / 100).toLocaleString("en-IN") : "99"}
              </span>
            </div>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-[#4A5568]">
            <li className="flex gap-2"><Sparkles size={14} className="mt-1 text-gold" /> Structured legal Will format</li>
            <li className="flex gap-2"><Sparkles size={14} className="mt-1 text-gold" /> Executor &amp; witness sections</li>
            <li className="flex gap-2"><Sparkles size={14} className="mt-1 text-gold" /> PDF &amp; DOCX downloads</li>
            <li className="flex gap-2"><Sparkles size={14} className="mt-1 text-gold" /> Future edits included</li>
            <li className="flex gap-2"><Sparkles size={14} className="mt-1 text-gold" /> Secure resume link</li>
          </ul>
        </div>

        {/* Lawyer Review upsell */}
        <div
          className="mt-4 rounded-md border border-gold/40 bg-[#FBF7EE] p-5"
          data-testid="payment-lawyer-upsell"
        >
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="font-serif text-lg text-navy">
                Add Senior Lawyer-Drafted Will – {formatRupees(config?.lawyer_review_paise) || "₹14,999"}
              </div>
              <p className="mt-1 text-sm text-[#4A5568]">
                Our legal consultants are senior advocates specialising exclusively in succession, testamentary and estate planning law — ensuring every Will meets the highest legal standard.
              </p>
            </div>
            <CallbackDialog
              label="Request Callback"
              plan="Lawyer Review"
              variant="outline"
              className="border-navy/30 text-navy hover:border-gold hover:bg-gold/10"
              testId="payment-lawyer-callback"
              buttonTestId="payment-lawyer-callback-btn"
            />
          </div>
        </div>

        {config?.razorpay_configured && /rzp_test_/.test(config?.razorpay_key_id || "") && (
          <div
            className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900"
            data-testid="payment-test-mode-tip"
          >
            <div className="font-semibold uppercase tracking-[0.18em] text-amber-700">
              Test mode active — recommended test path
            </div>
            <ul className="mt-1.5 ml-4 list-disc space-y-1 leading-relaxed">
              <li>
                <span className="font-semibold">Netbanking</span> → choose any test bank →
                click <span className="font-mono">Success</span> on the simulator (works without
                any extra Razorpay account settings).
              </li>
              <li>
                <span className="font-semibold">UPI</span> ID{" "}
                <code className="rounded bg-amber-100 px-1.5 py-0.5">success@razorpay</code>
                {" "}— only if UPI is enabled in your Razorpay dashboard.
              </li>
              <li>
                <span className="font-semibold">Cards</span> may show "International cards not
                supported" until International Payments is activated on your Razorpay merchant
                profile (Settings → Payment Methods).
              </li>
            </ul>
          </div>
        )}

        <div className="mt-6 flex items-start gap-3 rounded-md border border-[#0A1128]/10 bg-[#F9FAFB] p-4">
          <Checkbox
            id="payment-tos"
            checked={agreed}
            onCheckedChange={(v) => setAgreed(!!v)}
            data-testid="payment-tos-checkbox"
          />
          <label htmlFor="payment-tos" className="cursor-pointer text-sm text-[#4A5568]">
            I confirm that I have read and agreed to the{" "}
            <Link to="/terms" target="_blank" className="font-medium text-navy underline">
              Terms &amp; Conditions
            </Link>
            ,{" "}
            <Link to="/disclaimer" target="_blank" className="font-medium text-navy underline">
              Disclaimer
            </Link>{" "}
            and the strictly{" "}
            <Link to="/refund-policy" target="_blank" className="font-medium text-navy underline">
              No-Refund Policy
            </Link>
            .
          </label>
        </div>

        <Button
          size="lg"
          onClick={startPayment}
          disabled={loading || !agreed}
          className="mt-6 w-full bg-gold text-navy hover:bg-[#E5C87A] disabled:cursor-not-allowed disabled:opacity-60"
          data-testid="payment-pay-btn"
        >
          {loading
            ? "Opening secure checkout…"
            : `Pay ${formatRupees(price) || "₹99"} & Unlock My Will`}
        </Button>

        <p
          className="mt-3 text-center font-serif text-sm italic text-navy/80"
          data-testid="payment-trust-line"
        >
          "The most expensive Will is the one never written."
        </p>

        <div className="mt-4 flex flex-col items-center gap-2 text-center text-xs text-[#4A5568] sm:flex-row sm:justify-between sm:text-left">
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} className="text-gold" />
            Secure payments powered by Razorpay
          </div>
          <CallbackDialog
            label="Need help before payment? Speak to our team"
            plan="Essential Will"
            variant="ghost"
            className="px-0 text-xs text-navy underline hover:bg-transparent"
            testId="payment-help-callback"
            buttonTestId="payment-help-callback-btn"
          />
        </div>
      </CardContent>
    </Card>
  );
}
